import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row, Tooltip, message } from 'antd-v5';
import dayjs from 'dayjs';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Form, Input, Modal, Table } from '@/components/index';
import addIcon from '@/common/images/add.png';
import {
  addProductCorrectionDictRequest,
  deleteProductCorrectionDictRequest,
  getProductCorrectionDictPageRequest,
  updateProductCorrectionDictRequest,
} from '@/state/request/doubao';

import './index.less';

const DEFAULT_QUERY = {
  pageNo: 1,
  pageSize: 10,
};

const ProductCorrectionDictPage = () => {
  const [searchForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const loadData = useCallback(async (nextQuery = query) => {
    setLoading(true);
    try {
      const response = await getProductCorrectionDictPageRequest(nextQuery);
      if (response?.code === 200) {
        setPageData(response.data || {});
      } else {
        setPageData({});
      }
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    loadData(DEFAULT_QUERY);
  }, []);

  const openCreateModal = () => {
    setEditingRecord(null);
    editForm.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      sourceWord: record.sourceWord,
      targetWord: record.targetWord,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    editForm.resetFields();
  };

  const handleSearch = (values) => {
    const nextQuery = {
      ...DEFAULT_QUERY,
      ...values,
    };
    setQuery(nextQuery);
    loadData(nextQuery);
  };

  const handleReset = () => {
    searchForm.resetFields();
    setQuery(DEFAULT_QUERY);
    loadData(DEFAULT_QUERY);
  };

  const handleSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setSubmitLoading(true);
      const payload = editingRecord ? { ...values, id: editingRecord.id } : values;
      const response = editingRecord
        ? await updateProductCorrectionDictRequest(payload)
        : await addProductCorrectionDictRequest(payload);
      if (response?.code === 200) {
        message.success(editingRecord ? '修改成功' : '新增成功');
        closeModal();
        const nextQuery = editingRecord ? query : DEFAULT_QUERY;
        setQuery(nextQuery);
        loadData(nextQuery);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const response = await deleteProductCorrectionDictRequest(id);
    if (response?.code === 200) {
      message.success('删除成功');
      loadData(query);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '待纠正词',
        dataIndex: 'sourceWord',
        key: 'sourceWord',
        width: 260,
        render: (text) => (
          <Tooltip title={text} placement='topLeft'>
            <span className='text-ellipsis-2'>{text || '--'}</span>
          </Tooltip>
        ),
      },
      {
        title: '标准商品词',
        dataIndex: 'targetWord',
        key: 'targetWord',
        width: 260,
        render: (text) => (
          <Tooltip title={text} placement='topLeft'>
            <span className='text-ellipsis-2'>{text || '--'}</span>
          </Tooltip>
        ),
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180,
        render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      {
        title: '更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        width: 180,
        render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        align: 'center',
        width: 120,
        render: (_, record) => (
          <div className='product-correction-dict-actions'>
            <button type='button' onClick={() => openEditModal(record)}>
              编辑
            </button>
            <button
              type='button'
              className='danger'
              onClick={() => {
                modal.confirm({
                  className: 'ui-modal',
                  title: '确认删除词条？',
                  icon: null,
                  okText: '确认删除',
                  cancelText: '取消',
                  okType: 'danger',
                  content: (
                    <span>
                      删除后，语音开单识别纠正将不再使用该词条。
                      <br />
                      <br />
                      确认删除 <span style={{ fontWeight: 'bold' }}>{record.sourceWord}</span> 吗？
                    </span>
                  ),
                  onOk: () => handleDelete(record.id),
                });
              }}
            >
              删除
            </button>
          </div>
        ),
      },
    ],
    [modal, query],
  );

  const pagination = useMemo(
    () => ({
      current: pageData.pageNo || query.pageNo,
      pageSize: pageData.pageSize || query.pageSize,
      total: pageData.totalSize || 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`,
    }),
    [pageData, query],
  );

  const handleTableChange = (nextPagination) => {
    const nextQuery = {
      ...query,
      pageNo: nextPagination?.current || 1,
      pageSize: nextPagination?.pageSize || query.pageSize,
    };
    setQuery(nextQuery);
    loadData(nextQuery);
  };

  const formItemLayout = {
    labelCol: { flex: '82px' },
    wrapperCol: { flex: 'auto', width: 0 },
  };

  const topColResponsiveProps = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 8,
    xxl: 6,
  };

  return (
    <PageHeaderLayout>
      <div className='product-correction-dict-container'>
        <Card bordered={false} bodyStyle={{ padding: '0 16px 16px 16px' }}>
          <Form form={searchForm} layout='inline' onFinish={handleSearch} {...formItemLayout}>
            <Row style={{ width: '100%' }}>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='待纠正词' name='sourceWord'>
                  <Input style={{ width: '100%' }} allowClear placeholder='输入识别错误词' maxLength={100} />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='标准商品词' name='targetWord'>
                  <Input style={{ width: '100%' }} allowClear placeholder='输入标准商品词' maxLength={100} />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16, paddingRight: 8 }}>
                <div className='product-correction-dict-toolbar'>
                  <Button type='primary' htmlType='submit'>
                    查询
                  </Button>
                  <Button onClick={handleReset}>重置</Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card bordered={false} style={{ marginTop: 8 }}>
          <div className='product-correction-dict-card-header'>
            <Button type='primary' onClick={openCreateModal}>
              <div className='product-correction-dict-add'>
                <img src={addIcon} alt='add' />
                新增词条
              </div>
            </Button>
          </div>
          <Table
            rowKey='id'
            loading={loading}
            columns={columns}
            dataSource={pageData.pageList || []}
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </Card>

        <Modal
          className='ui-modal'
          title={editingRecord ? '编辑词条' : '新增词条'}
          open={modalOpen}
          onCancel={closeModal}
          footer={
            <div>
              <Button onClick={closeModal}>取消</Button>
              <Button type='primary' loading={submitLoading} onClick={handleSubmit}>
                确认
              </Button>
            </div>
          }
          destroyOnClose
          width={520}
          maskClosable={false}
        >
          <Form
            form={editForm}
            style={{ padding: '12px 0', marginBottom: -16 }}
            labelCol={{ flex: '112px' }}
            wrapperCol={{ flex: '332px' }}
            colon={false}
          >
            <Form.Item
              label='待纠正词'
              name='sourceWord'
              rules={[{ required: true, message: '请输入待纠正词' }]}
            >
              <Input placeholder='请输入待纠正词' maxLength={100} />
            </Form.Item>
            <Form.Item
              label='标准商品词'
              name='targetWord'
              rules={[{ required: true, message: '请输入标准商品词' }]}
            >
              <Input placeholder='请输入标准商品词' maxLength={100} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
      {contextHolder}
    </PageHeaderLayout>
  );
};

export default ProductCorrectionDictPage;
